import StyledButton from '@/components/styled/StyledButton';
import StyledButtonLoader from '@/components/styled/StyledButtonLoader';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import StyledStepper from '@/components/styled/StyledStepper';
import { PATHS } from '@/constants/Paths';
import { buildBenefitsListSearch } from '@/utils/searchParams/buildBenefitsListSearch';
import { getListSearchFromLocation } from '@/utils/searchParams/getListSearchFromLocation';
import { zodResolver } from '@hookform/resolvers/zod';
import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import {
  PastedFieldsProvider,
  usePastedFieldsState,
} from '@/contexts/PastedFieldsContext';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { useGetAllMySuppliers } from '@/api/supplier.api';
import { useCreateHotel, useUpdateHotel } from '@/hooks/useHotel';
import { getHotelCategoryName } from '@/utils/benefits/getHotelCategoryName';
import { ArrowRightIcon } from 'lucide-react';
import { getRoomPensionOptions } from '@/constants/hotelPension';
import HotelRoomsCategoriesList from '@/components/hotel/roomCategories/HotelRoomsCategoriesList';
import { WEEKDAYS } from '@/constants/weekdays';
import HotelPricingSummary from '@/components/hotel/HotelPricingSummary';
import clsx from 'clsx';
import { SupplierStatus } from '@/types/suppliers/SupplierStatus';
import { getAllPricesPartitions } from '@/utils/benefits/getHotelPrices';
import { useToast } from '@/hooks/useToast';
import HotelConfigurationStep from '@/components/hotel/HotelConfigurationStep';
import { numberIfNotUndefined } from '@/utils/prices/numberIfNotUndefined';
import { buildEmptyPensionCard } from '@/utils/benefits/hotel/buildEmptyPensionCard';
import { serializePensionPricings } from '@/utils/benefits/hotel/serializePensionPricings';
import { useTranslation } from '@/i18n';
import { HOTEL_STEPS } from '@/constants/steppers/hotelSteps';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import {
  HotelFormDataWithTranslations,
  HotelSchemaWithTranslations,
} from '@/i18n/translation-type-utils';
import { useResourceLanguage } from '@/hooks/useResourceLanguage';
import { useTranslateFields } from '@/hooks/useTranslate';
import LanguageSwitchWithTranslateBtn from '@/components/translation/LanguageSwitchWithTranslateBtn';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';
import { buildEmptyHotelSupplement } from '@/utils/benefits/hotel/buildEmptyHotelSupplementCard';
import { serializeHotelSupplements } from '@/utils/benefits/hotel/serializeHotelSupplements';

type SubmitFormType = (
  isDraft?: boolean,
) => SubmitHandler<HotelFormDataWithTranslations>;

const CreateHotelPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const methods = useForm<HotelFormDataWithTranslations>({
    mode: 'onChange',
    resolver: zodResolver(HotelSchemaWithTranslations),
    defaultValues: {
      name: undefined,
      category: undefined,
      chain: undefined,
      supplier: undefined,
      currency: undefined,
      country: undefined,
      marginRate: undefined,
      address: undefined,
      postalCode: undefined,
      city: undefined,
      latitude: undefined,
      longitude: undefined,
      contacts: {
        coordinates: [{ name: undefined, phoneNumber: undefined }],
        website: undefined,
        mainEmail: undefined,
        reservationsEmail: undefined,
        stockAlertEmail: undefined,
        secondaryEmails: [],
      },
      mainImage: undefined,
      images: [],
      shortDescriptionTitle: undefined,
      shortDescriptionBody: undefined,
      longDescriptionTitle: undefined,
      longDescriptionBody: undefined,
      acceptsChildPricing: 'false',
      childAges: [
        {
          childAgeFrom: 2,
          childAgeTo: 11,
        },
      ],
      pricingType: 'byRoom',
      // Default to 'false' (not undefined) so the "Non" radio shows checked
      // by default when the user switches pricingType to 'byPerson', matching
      // every other Yes/No radio in this section.
      reducedPriceExists: 'false',
      priceReductionType: undefined,
      pricingPensionType: getRoomPensionOptions(t)[0],
      shouldFillOtherPensionTypes: 'false',
      areOtherPensionPricesCommon: 'false',
      commonPensionPricings: [buildEmptyPensionCard()],
      shouldFillHotelSupplements: 'false',
      hotelSupplements: [buildEmptyHotelSupplement()],
      shouldAssociateBenefits: 'false',
      expertMode: 'false',
      associatedBenefits: [],
      hotelRoomCategories: [
        {
          images: [],
          categoryPensionPricings: [buildEmptyPensionCard()],
          roomTypes: {
            individual: false,
            double: false,
            triple: false,
            quadruple: false,
          },
          individualRoomOptions: [0],
          doubleRoomOptions: [0],
          tripleRoomOptions: [],
          quadrupleRoomOptions: [],
          tarifs: [
            {
              periods: [{ applicationDays: WEEKDAYS }],
              defaultPricesExist: 'false',
              defaultPrices: {
                price: undefined,
                firstSpecialPrice: undefined,
                secondSpecialPrice: undefined,
              },
              pricePartitions: {
                individual: [],
                double: [],
                triple: [],
                quadruple: [],
              },
            },
          ],
        },
      ],
    },
  });

  const {
    control,
    getValues,
    handleSubmit,
    formState: { isValid, errors },
    setValue,
  } = methods;

  const currentName = useWatch({ control, name: 'name' });
  const selectedSupplier = useWatch({ control, name: 'supplier' });
  const selectedCountry = useWatch({ control, name: 'country' });

  const [currStep, setCurrStep] = useState(0);
  const [benefitId, setBenefitId] = useState<string | null>(null);
  const { data: suppliers } = useGetAllMySuppliers();
  const { mutateAsync: createHotel } = useCreateHotel();
  const { mutateAsync: updateHotel } = useUpdateHotel();

  const errorsLength = useMemo(() => Object.keys(errors).length, [errors]);

  const { language, setLanguage } = useResourceLanguage();
  const { translateFields, isTranslating } = useTranslateFields({
    targetLanguage: language,
    control,
    setValue,
    fields: [
      'benefit.name',
      'shortDescriptionTitle',
      'shortDescriptionBody',
      'longDescriptionTitle',
      'longDescriptionBody',
    ] as const,
    nestedFields: {
      hotelRoomCategories: ['name', 'shortDescription', 'longDescription'],
    },
  });

  const categoryErrorIndex = useMemo(() => {
    if (errors.hotelRoomCategories) {
      return +Object.keys(errors.hotelRoomCategories)[0];
    }
    return null;
  }, [errors.hotelRoomCategories]);

  const hasNavigatedToErrorStepRef = useRef(false);
  const prevHasErrorsRef = useRef(false);

  useEffect(() => {
    const hasErrors = !!errors.hotelRoomCategories;

    if (
      hasErrors &&
      !prevHasErrorsRef.current &&
      !hasNavigatedToErrorStepRef.current
    ) {
      startTransition(() => {
        setCurrStep(1);
      });
      hasNavigatedToErrorStepRef.current = true;
    }

    // Reset navigation flag when errors clear
    if (!hasErrors && prevHasErrorsRef.current) {
      hasNavigatedToErrorStepRef.current = false;
    }

    prevHasErrorsRef.current = hasErrors;
  }, [errors.hotelRoomCategories]);

  const { toastAsync } = useToast();
  const pastedFieldsState = usePastedFieldsState();

  const submitForm: SubmitFormType =
    (isDraft = true) =>
    async (data) => {
      const formData = new FormData();
      const addIf = (value: string | Blob | undefined, key: string) => {
        if (value !== undefined) formData.append(key, value);
      };
      if (!isDraft) formData.append('status', SupplierStatus.PUBLISHED);
      addIf(data.name, 'name');
      if (data.category)
        formData.append(
          'category',
          getHotelCategoryName(t, data.category.value),
        );
      if (data.chain) formData.append('chain', data.chain.value);
      addIf(data.supplier?.value, 'supplierId');
      addIf(data.currency?.value, 'currency');
      addIf(data.country?.value, 'country');
      const parsedMarginRate = MarginRateSchema.safeParse(data.marginRate);
      if (parsedMarginRate.success && parsedMarginRate.data !== undefined) {
        formData.append('marginRate', String(parsedMarginRate.data));
      }
      addIf(data.address, 'address');
      addIf(data.postalCode, 'postalCode');
      if (data.city) formData.append('city', data.city.value);
      if (data.latitude) formData.append('latitude', String(data.latitude));
      if (data.longitude) formData.append('longitude', String(data.longitude));
      if (data.mainImage) {
        formData.append('files', data.mainImage);
        formData.append('hasMainImage', true.toString());
      } else formData.append('hasMainImage', false.toString());
      if (data.images) {
        data.images
          .filter((image) => image instanceof Blob)
          .forEach((image) => formData.append('files', image));
        formData.append(
          'imagesCount',
          data.images
            .filter((image) => image instanceof Blob)
            .length.toString(),
        );
      }
      addIf(data.shortDescriptionTitle, 'shortDescriptionTitle');
      addIf(data.shortDescriptionBody, 'shortDescriptionBody');
      addIf(data.longDescriptionTitle, 'longDescriptionTitle');
      addIf(data.longDescriptionBody, 'longDescriptionBody');
      if (data.contacts?.coordinates && data.contacts.coordinates.length > 0) {
        formData.append(
          'contactNames',
          JSON.stringify(
            data.contacts.coordinates
              .filter((coordinate) => coordinate.name && coordinate.phoneNumber)
              .map((contact) => contact.name),
          ),
        );
        formData.append(
          'contactPhones',
          JSON.stringify(
            data.contacts.coordinates
              .filter((coordinate) => coordinate.name && coordinate.phoneNumber)
              .map((contact) => contact.phoneNumber),
          ),
        );
      }
      addIf(data.contacts?.website, 'contactWebsite');
      addIf(data.contacts?.mainEmail, 'contactMainEmail');
      addIf(data.contacts?.reservationsEmail, 'contactReservationsEmail');
      addIf(data.contacts?.stockAlertEmail, 'contactStockAlertEmail');
      if (data.contacts?.secondaryEmails)
        formData.append(
          'contactSecondaryEmails',
          JSON.stringify(
            data.contacts.secondaryEmails.map(({ email }) => email),
          ),
        );
      addIf(data.acceptsChildPricing, 'acceptsChildPricing');
      if (data.childAges && data.childAges.length > 0) {
        formData.append('childAgeFrom', String(data.childAges[0].childAgeFrom));
        formData.append('childAgeTo', String(data.childAges[0].childAgeTo));
        if (data.childAges.length > 1) {
          formData.append(
            'secondChildAgeFrom',
            String(data.childAges[1].childAgeFrom),
          );
          formData.append(
            'secondChildAgeTo',
            String(data.childAges[1].childAgeTo),
          );
        }
      }
      if (data.pricingPensionType)
        formData.append('pricingPensionType', data.pricingPensionType.value);
      addIf(data.shouldFillOtherPensionTypes, 'shouldFillOtherPensionTypes');
      addIf(data.areOtherPensionPricesCommon, 'areOtherPensionPricesCommon');
      if (data.commonPensionPricings) {
        formData.append(
          'commonPensionPricings',
          JSON.stringify(serializePensionPricings(data.commonPensionPricings)),
        );
      }
      addIf(data.pricingType, 'pricingType');
      addIf(data.reducedPriceExists, 'reducedPriceExists');
      if (data.priceReductionType)
        formData.append(
          'isReduction',
          (data.priceReductionType === 'reduction').toString(),
        );
      addIf(data.shouldFillHotelSupplements, 'shouldFillHotelSupplements');
      if (data.hotelSupplements) {
        formData.append(
          'hotelSupplements',
          JSON.stringify(serializeHotelSupplements(data.hotelSupplements)),
        );
      }
      addIf(data.shouldAssociateBenefits, 'shouldAssociateBenefits');
      addIf(data.expertMode, 'expertMode');
      formData.append(
        'associatedBenefits',
        JSON.stringify(
          (data.associatedBenefits ?? []).map(({ benefitId, frequency }) => ({
            benefitId,
            frequency,
          })),
        ),
      );
      if (data.hotelRoomCategories) {
        formData.append(
          'hotelRoomCategories',
          JSON.stringify(
            data.hotelRoomCategories.map((roomCategory) => ({
              ...roomCategory,
              categoryPensionPricings: serializePensionPricings(
                roomCategory.categoryPensionPricings ?? [],
              ),
              pricings: roomCategory.tarifs.map((tarif) => ({
                name: tarif.name,
                defaultPricesExists: tarif.defaultPricesExist,
                defaultAdultPrice: numberIfNotUndefined(
                  tarif.defaultPrices?.price,
                ),
                defaultFirstSpecialPrice: numberIfNotUndefined(
                  tarif.defaultPrices?.firstSpecialPrice,
                ),
                defaultSecondSpecialPrice: numberIfNotUndefined(
                  tarif.defaultPrices?.secondSpecialPrice,
                ),
                periods: tarif.periods.map((period) => ({
                  startDate: period.startDate
                    ? new Date(period.startDate)
                    : undefined,
                  endDate: period.endDate
                    ? new Date(period.endDate)
                    : undefined,
                  applicationDays: period.applicationDays,
                })),
                pricePartitions: getAllPricesPartitions(
                  tarif.pricePartitions,
                  roomCategory.roomTypes,
                ),
              })),
              tarifs: undefined,
              category: roomCategory.category?.value ?? undefined,
              complementaryInformation:
                roomCategory.complementaryInformation?.value ?? undefined,
              roomTypes: Object.keys(roomCategory.roomTypes).filter(
                (key) =>
                  roomCategory.roomTypes[
                    key as keyof typeof roomCategory.roomTypes
                  ],
              ),
              hasMainImage: roomCategory.mainImage !== undefined,
              imagesCount: roomCategory.images?.length ?? 0,
              images: undefined,
              mainImage: undefined,
            })),
          ),
        );
        data.hotelRoomCategories.forEach((roomCategory) => {
          if (roomCategory.mainImage instanceof Blob)
            formData.append('files', roomCategory.mainImage);
          roomCategory.images
            .filter((image) => image instanceof Blob)
            .forEach((image) => formData.append('files', image));
        });
      }
      formData.append('translations', JSON.stringify(data.translations));
      if (!benefitId) {
        const result = await toastAsync(createHotel(formData), {
          success: t('benefits.benefitCreatedSuccess'),
        });
        setBenefitId(result.data.benefitId);
      } else {
        await toastAsync(updateHotel({ benefitId, data: formData }), {
          success: t('benefits.benefitUpdatedSuccess'),
        });
      }
      pastedFieldsState.clearPasted();
      if (!isDraft)
        navigate(
          PATHS.home.benefits.list +
            getListSearchFromLocation(location, buildBenefitsListSearch),
        );
    };

  return (
    <FormProvider {...methods}>
      <PastedFieldsProvider value={pastedFieldsState}>
        <StyledContainer>
          <StyledFormHeaderWithBreadcrumb
            title={currentName || t('hotels.newHotel')}
            breadcrumb={
              <StyledButton
                variant="link"
                title={t('common.backToList')}
                className="text-sm text-[#3D3D3D] pl-0"
                onClick={() =>
                  navigate(
                    PATHS.home.benefits.list +
                      getListSearchFromLocation(
                        location,
                        buildBenefitsListSearch,
                      ),
                  )
                }
                icon={<ArrowLeftIcon />}
              />
            }
          >
            <StyledButton
              title={t('common.cancel')}
              onClick={() =>
                navigate(
                  PATHS.home.benefits.list +
                    getListSearchFromLocation(
                      location,
                      buildBenefitsListSearch,
                    ),
                )
              }
              variant="outlined"
              className="font-semibold gap-1 px-3 py-2"
            />
            <StyledButtonLoader
              title={t('common.saveAsDraft')}
              successTitle={t('common.draftSaved')}
              disabled={
                currentName == undefined ||
                selectedSupplier == undefined ||
                selectedCountry == undefined
              }
              className="font-semibold gap-1 px-3 py-2"
              onClick={() => submitForm(true)(getValues())}
              icon={<SaveIcon className="w-5 h-5" />}
            />
          </StyledFormHeaderWithBreadcrumb>
          <StyledStepper
            activeStep={currStep}
            steps={getTranslatedConst(HOTEL_STEPS)}
            setStep={(step: number) => setCurrStep(step)}
          />
          <LanguageSwitchWithTranslateBtn
            translateFields={() =>
              translateFields({
                allNestedFields: true,
                translateToAllLocales: true,
              })
            }
            isTranslating={isTranslating}
            language={language}
            setLanguage={setLanguage}
            hideTranslateBtn={currStep > 1}
          />
          {currStep === 0 ? (
            <>
              <HotelConfigurationStep
                control={control}
                language={language}
                translateFields={(fields) =>
                  translateFields({ fieldsToTranslate: fields })
                }
                isTranslating={isTranslating}
                errorsLength={errorsLength}
                suppliers={suppliers}
              />
              <div className="flex flex-row-reverse px-6 pb-6 md:w-fit md:sticky md:left-full md:bottom-16">
                <StyledButtonLoader
                  title={t('common.saveAsDraft')}
                  successTitle={t('common.draftSaved')}
                  disabled={
                    currentName == undefined ||
                    selectedSupplier == undefined ||
                    selectedCountry == undefined
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  onClick={() => submitForm(true)(getValues())}
                  icon={<SaveIcon className="w-5 h-5" />}
                />
              </div>
              <div className="flex flex-row-reverse p-6 md:w-fit md:sticky md:left-full md:bottom-0">
                <StyledButton
                  title={t('common.validateAndCreateRoomCategory')}
                  onClick={() => {
                    setCurrStep(1);
                    window.scrollTo(0, 0);
                  }}
                  disabled={
                    currentName == undefined ||
                    selectedSupplier == undefined ||
                    selectedCountry == undefined
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<ArrowRightIcon className="w-5 h-5" />}
                  iconPosition="right"
                />
              </div>
            </>
          ) : currStep === 1 ? (
            <>
              <HotelRoomsCategoriesList
                control={control}
                categoryErrorIndex={categoryErrorIndex}
                language={language}
              />
              <div className="flex flex-row-reverse px-6 pb-6 md:w-fit md:sticky md:left-full md:bottom-16">
                <StyledButtonLoader
                  title={t('common.saveAsDraft')}
                  successTitle={t('common.draftSaved')}
                  disabled={
                    currentName == undefined ||
                    selectedSupplier == undefined ||
                    selectedCountry == undefined
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  onClick={() => submitForm(true)(getValues())}
                  icon={<SaveIcon className="w-5 h-5" />}
                />
              </div>
              <div className="flex flex-row-reverse px-6 pb-6 md:w-fit md:sticky md:left-full md:bottom-0">
                <StyledButton
                  title={t('common.summary')}
                  icon={<ArrowRightIcon className="w-5 h-5" />}
                  iconPosition="right"
                  className="font-semibold gap-1 px-3 py-2"
                  onClick={() => {
                    setCurrStep(2);
                    window.scrollTo(0, 0);
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <HotelPricingSummary control={control} />
              <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10 bg-white">
                <StyledButtonLoader
                  title={t('hotels.publishHotel')}
                  className="font-semibold gap-1 px-3 py-2"
                  onClick={handleSubmit(submitForm(false))}
                  successTitle={t('common.publishedSuccess')}
                  hasSucceeded={!errorsLength}
                  errorTitle={t('common.checkRequiredFields')}
                  icon={
                    <PlusIcon
                      className={clsx('w-5 h-5', {
                        'fill-white': isValid,
                        'fill-gray-400': !isValid,
                      })}
                    />
                  }
                  iconPosition="right"
                />
              </div>
            </>
          )}
        </StyledContainer>
      </PastedFieldsProvider>
    </FormProvider>
  );
};

export default CreateHotelPage;
